import { Camera, Cloud, Droplets, Moon, Sparkles, Sunset as SunsetIcon, Telescope } from 'lucide-react';
import { cloudGuidance, computeImagingWindow, dewGuidance, stargazingScore } from '../lib/astro';
import type { DayBlock, HourlyEntry, PlanetVisibility, SunsetQuality } from '../types';

const RISK_TEXT = {
  low: 'text-emerald-300',
  moderate: 'text-amber-300',
  high: 'text-rose-300',
} as const;

export default function AstroTonight({
  day,
  hourly,
  timeZone,
  sunsetQuality,
  planets,
}: {
  day: DayBlock;
  hourly: HourlyEntry[];
  timeZone: string;
  sunsetQuality: SunsetQuality | null;
  planets: PlanetVisibility[];
}) {
  const imaging = computeImagingWindow(day, hourly);
  const star = stargazingScore(day, imaging?.clouds.avgPct ?? null);
  const guidance = imaging ? dewGuidance(imaging.dewRisk) : null;
  const cloudBanner = imaging ? cloudGuidance(imaging.clouds) : null;

  const fmt = (iso: string) =>
    new Date(iso).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
    });

  return (
    <section>
      <h2 className="text-xs uppercase tracking-wider opacity-70 mb-2 flex items-center gap-2">
        <Telescope className="w-3.5 h-3.5" /> Tonight for astro
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-70 mb-1">
            <SunsetIcon className="w-3.5 h-3.5" /> Sunset
          </div>
          {sunsetQuality ? (
            <div className="space-y-0.5">
              <div className="text-sm">
                {sunsetQuality.emoji}{' '}
                <span className="font-medium capitalize">{sunsetQuality.label}</span>{' '}
                <span className="opacity-70 text-xs">
                  · {day.cloudCoverPct ?? '—'}% cloud
                </span>
              </div>
              <div className="text-xs opacity-80">{fmt(day.sunset)}</div>
            </div>
          ) : (
            <div className="text-sm opacity-80">{fmt(day.sunset)}</div>
          )}
        </div>

        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-70 mb-1">
            <Moon className="w-3.5 h-3.5" /> Dark-sky score
          </div>
          <div className="flex items-baseline gap-2">
            <div className="text-xl font-medium">
              {'★'.repeat(star.score)}
              <span className="opacity-30">{'★'.repeat(5 - star.score)}</span>
            </div>
            <div className="text-sm">{star.label}</div>
          </div>
          <div className="text-xs opacity-75">
            {day.moonPhaseName} · {day.moonIllumPct}% illum
          </div>
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 mt-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-70 mb-2">
          <Camera className="w-3.5 h-3.5" /> Imaging window
        </div>
        {imaging ? (
          <div className="space-y-1.5">
            <div className="text-sm">
              <span className="font-medium tabular-nums">
                {fmt(imaging.start)} – {fmt(imaging.end)}
              </span>
              <span className="opacity-70"> · {imaging.hours.toFixed(1)}h</span>
            </div>
            <div className="text-xs opacity-80">
              {imaging.moonDownTheWholeTime
                ? 'Moon below horizon the whole window.'
                : `Moon ${day.moonSet && new Date(day.moonSet) <= new Date(imaging.start) ? 'already set' : `rises ${day.moonRise ? fmt(day.moonRise) : '—'}`}.`}
            </div>
          </div>
        ) : (
          <div className="text-sm opacity-80">
            Bright moon up through dark hours — no great moonless window tonight.
          </div>
        )}
      </div>

      {imaging && cloudBanner && (
        <div className={`rounded-lg p-3 mt-2 border backdrop-blur-sm ${cloudBanner.tone}`}>
          <div className="flex items-start gap-2">
            <Cloud className="w-5 h-5 shrink-0 mt-0.5 opacity-80" />
            <div className="flex-1 space-y-0.5">
              <div className="text-sm font-medium">{cloudBanner.headline}</div>
              <div className="text-xs opacity-90">{cloudBanner.detail}</div>
              {imaging.clouds.minPct != null && imaging.clouds.maxPct != null && (
                <div className="text-[10px] opacity-60">
                  Cloud range: {imaging.clouds.minPct}% – {imaging.clouds.maxPct}%
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {imaging && guidance && (
        <div
          className={`rounded-lg p-3 mt-2 border backdrop-blur-sm ${guidance.tone}`}
        >
          <div className="flex items-start gap-2">
            <Droplets className={`w-5 h-5 shrink-0 mt-0.5 ${RISK_TEXT[imaging.dewRisk]}`} />
            <div className="flex-1 space-y-1.5">
              <div className="text-sm font-medium">{guidance.headline}</div>

              <div className="text-xs opacity-90 tabular-nums">
                {imaging.dewOnsetAt && imaging.dewRisk !== 'low' && (
                  <span>
                    Dew onset ~{fmt(imaging.dewOnsetAt)}
                    {imaging.peakDewHour && imaging.peakDewHour !== imaging.dewOnsetAt && (
                      <>
                        {' · peak '}
                        {fmt(imaging.peakDewHour)}
                        {imaging.peakDewSpreadF != null && (
                          <span className="opacity-70">
                            {' '}
                            (spread {imaging.peakDewSpreadF.toFixed(1)}°)
                          </span>
                        )}
                      </>
                    )}
                  </span>
                )}
                {imaging.maxHumidity != null && (
                  <span className="ml-2 opacity-70">
                    peak RH {imaging.maxHumidity}%
                  </span>
                )}
              </div>

              {guidance.tips.length > 0 && (
                <ul className="text-xs opacity-90 space-y-0.5 list-disc list-inside pl-0.5">
                  {guidance.tips.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              )}

              {imaging.hourly.length > 0 && (
                <div className="pt-2">
                  <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1">
                    Hourly dew risk
                  </div>
                  <div className="flex gap-px h-6 rounded overflow-hidden">
                    {imaging.hourly.map((h) => (
                      <div
                        key={h.time}
                        title={`${fmt(h.time)} · spread ${h.spreadF ?? '—'}° · RH ${h.humidity ?? '—'}%`}
                        className={`flex-1 ${
                          h.risk === 'high'
                            ? 'bg-rose-400/80'
                            : h.risk === 'moderate'
                              ? 'bg-amber-400/70'
                              : 'bg-emerald-400/60'
                        }`}
                      />
                    ))}
                  </div>
                  <div className="flex justify-between text-[10px] opacity-60 mt-0.5 tabular-nums">
                    <span>{fmt(imaging.hourly[0].time)}</span>
                    <span>{fmt(imaging.hourly[imaging.hourly.length - 1].time)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {imaging && <CloudTimeline hourly={hourly} windowStart={imaging.start} windowEnd={imaging.end} timeZone={timeZone} />}

      {planets.length > 0 && <PlanetsList planets={planets} timeZone={timeZone} />}

      <div className="text-[10px] opacity-50 mt-1.5">
        Astronomical twilight approximated at sunset + 90 min. Dew risk: high = spread &lt; 3°F or
        RH ≥ 95%, moderate = spread &lt; 6°F or RH ≥ 85%.
      </div>
    </section>
  );
}

function CloudTimeline({
  hourly,
  windowStart,
  windowEnd,
  timeZone,
}: {
  hourly: HourlyEntry[];
  windowStart: string;
  windowEnd: string;
  timeZone: string;
}) {
  const start = new Date(windowStart);
  const end = new Date(windowEnd);
  const windowed = hourly.filter((h) => {
    const t = new Date(h.time);
    return t >= start && t <= end;
  });
  if (windowed.length === 0) return null;

  const fmt = (iso: string) =>
    new Date(iso).toLocaleTimeString('en-US', {
      hour: 'numeric',
      timeZone,
    });

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 mt-2">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-70 mb-2">
        <Cloud className="w-3.5 h-3.5" /> Cloud cover during imaging window
      </div>
      <div className="flex items-end gap-px h-10">
        {windowed.map((h) => {
          const pct = h.cloudCoverPct ?? 0;
          const fillHeight = Math.max(4, pct);
          // Color gradient: clear = emerald (good), overcast = slate (bad)
          const hue = pct < 20 ? 'bg-emerald-400/80' : pct < 50 ? 'bg-sky-400/70' : pct < 80 ? 'bg-slate-400/70' : 'bg-slate-600/80';
          return (
            <div
              key={h.time}
              className="flex-1 relative"
              title={`${fmt(h.time)} · cloud ${pct}%${h.cloudCoverHighPct != null ? ` (high ${h.cloudCoverHighPct}%)` : ''}`}
            >
              <div className={`${hue} rounded-sm`} style={{ height: `${fillHeight}%` }} />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] opacity-60 mt-1 tabular-nums">
        <span>{fmt(windowed[0].time)}</span>
        <span>{fmt(windowed[windowed.length - 1].time)}</span>
      </div>
    </div>
  );
}

function PlanetsList({ planets, timeZone }: { planets: PlanetVisibility[]; timeZone: string }) {
  const fmt = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          timeZone,
        })
      : '—';

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 mt-2">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-70 mb-2">
        <Sparkles className="w-3.5 h-3.5" /> Planets tonight
      </div>
      <div className="space-y-1.5">
        {planets.map((p) => (
          <div
            key={p.name}
            className="flex items-center gap-3 text-xs tabular-nums border-t border-white/5 first:border-t-0 pt-1.5 first:pt-0"
          >
            <div className="w-16 font-medium">{p.name}</div>
            <div className="flex-1 opacity-85">
              rise {fmt(p.rise)} · transit {fmt(p.transit)} · set {fmt(p.set)}
            </div>
            <div className="text-right opacity-85 shrink-0">
              {p.maxAltitudeDeg != null && (
                <>
                  {p.maxAltitudeDeg}° {compass(p.azimuthAtMaxDeg)}
                </>
              )}
              {p.magnitude != null && (
                <span className="ml-2 opacity-60">mag {p.magnitude.toFixed(1)}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function compass(deg: number | null): string {
  if (deg == null) return '';
  const dirs = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  return dirs[Math.round(deg / 22.5) % 16];
}
